import { useCallback, useEffect, useState } from 'react';
import {
  CustomAnalysisPromptResponse,
  getCustomAnalysis,
} from 'services/actions/ai_management_custom_analysis';

export interface CustomAnalysisPrompt {
  id: number;
  content: string;
  createdAt: string | null;
  updatedAt: string | null;
}

interface State {
  loaded: boolean;
  prompt: CustomAnalysisPrompt | null;
  error: boolean;
}

const INITIAL_STATE: State = { loaded: false, prompt: null, error: false };

const toPrompt = (prompt: CustomAnalysisPromptResponse | null): CustomAnalysisPrompt | null => {
  if (!prompt || !prompt.content?.trim()) return null;

  return {
    id: prompt.id,
    content: prompt.content,
    createdAt: prompt.created_at ?? null,
    updatedAt: prompt.updated_at ?? null,
  };
};

export function useCustomAnalysisPrompt(enabled = true) {
  const [state, setState] = useState<State>(INITIAL_STATE);

  const load = useCallback((active: () => boolean) => {
    getCustomAnalysis()
      .then((response) => {
        if (!active()) return;
        setState({
          loaded: true,
          prompt: toPrompt(response?.data?.analysis_prompt ?? null),
          error: false,
        });
      })
      .catch((err) => {
        console.error('Erro ao carregar a análise personalizada:', err);
        if (active()) setState({ loaded: true, prompt: null, error: true });
      });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setState(INITIAL_STATE);
      return undefined;
    }

    let active = true;
    load(() => active);

    return () => {
      active = false;
    };
  }, [enabled, load]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    load(() => true);
  }, [enabled, load]);

  return {
    prompt: state.prompt,
    loading: enabled && !state.loaded,
    error: state.error,
    refetch,
  };
}
